import { additionalLocalizedPathnames } from "@/i18n/additional-localized-pathnames";
import { additionalNordicPathnames } from "@/i18n/additional-nordic-pathnames";

export const locales = [
  "en",
  "da",
  "no",
  "sv",
  "de",
  "fr",
  "it",
  "es",
  "pt",
  "el",
  "nl",
  "fi",
  "is",
  "fo",
] as const;

// Locales with a reviewed comparison (/sammenligning): dated cells, each
// backed by a source. The other locales' old translations made unsourced
// claims about named competitors, so /sammenligning shows them the English
// text (noindexed) until a reviewed translation exists.
export const REVIEWED_COMPARISON_LOCALES = [
  "en",
  "da",
  "sv",
  "no",
  "de",
] as const;

// Locales with a reviewed /wedbox page. It reuses the comparison's table, so
// it must stay a subset of REVIEWED_COMPARISON_LOCALES; the other locales
// redirect to their comparison page.
export const REVIEWED_WEDBOX_LOCALES = [
  "en",
  "da",
] as const satisfies readonly (typeof REVIEWED_COMPARISON_LOCALES)[number][];

// Locales with a reviewed /kululu-alternative page (written in English only);
// the other locales redirect to their comparison page.
export const REVIEWED_KULULU_LOCALES = [
  "en",
] as const satisfies readonly (typeof REVIEWED_COMPARISON_LOCALES)[number][];

const basePathnames = {
  "/": "/",
  "/login": "/login",
  "/privacy-policy": "/privacy-policy",
  "/terms-and-conditions": "/terms-and-conditions",
  "/home": "/home",
  "/home/profile": "/home/profile",
  "/home/thank-you": "/home/thank-you",
  "/home/[slug]": "/home/[slug]",
  "/home/[slug]/templates": "/home/[slug]/templates",
  "/home/[slug]/printsager": "/home/[slug]/printsager",
  "/home/[slug]/configurator": "/home/[slug]/configurator",
  "/home/upgrade": "/home/upgrade",
  "/upload/[slug]": "/upload/[slug]",
  "/embedded": "/embedded",
  "/wedbox": "/wedbox",
  "/kululu-alternative": "/kululu-alternative",
  "/blog": "/blog",
  "/sammenligning": {
    en: "/comparison",
    da: "/sammenligning",
    no: "/sammenligning",
    sv: "/jamforelse",
    de: "/vergleich",
  },
  "/how-to-use-qr-codes": {
    en: "/how-to-use-qr-codes",
    da: "/hvordan-bruges-qr-koder",
    no: "/hvordan-bruke-qr-koder",
    sv: "/hur-man-anvander-qr-koder",
    de: "/wie-man-qr-codes-verwendet",
  },
  "/how-it-works": {
    en: "/how-it-works",
    da: "/saadan-virker-det",
    no: "/slik-fungerer-det",
    sv: "/sa-fungerar-det",
    de: "/so-funktioniert-es",
  },
  "/shared-photo-album": {
    en: "/shared-photo-album",
    da: "/delt-fotoalbum",
    no: "/delt-fotoalbum",
    sv: "/delat-fotoalbum",
    de: "/geteiltes-fotoalbum",
  },
  "/printed-for-you": {
    en: "/printed-for-you",
    da: "/trykt-for-jer",
  },
  // Free QR sign maker; da, sv and no only (other locales 404).
  "/qr-skilt": {
    da: "/qr-skilt",
    sv: "/qr-skylt",
    no: "/qr-skilt",
  },
  // The withdrawal function (forbrugeraftaleloven § 20 a; approval 390). Every
  // locale has it; locales without their own wording use the English path.
  // Keep in step with WITHDRAWAL_PATHNAMES in src/lib/withdrawal.ts.
  "/fortryd-aftale": {
    en: "/withdraw-from-contract",
    da: "/fortryd-aftale",
    no: "/angre-avtalen",
    sv: "/angra-avtalet",
    de: "/vertrag-widerrufen",
    fr: "/withdraw-from-contract",
    it: "/withdraw-from-contract",
    es: "/withdraw-from-contract",
    pt: "/withdraw-from-contract",
    el: "/withdraw-from-contract",
    nl: "/withdraw-from-contract",
    fi: "/withdraw-from-contract",
    is: "/withdraw-from-contract",
    fo: "/withdraw-from-contract",
  },
  // The photo book page (concept c, 2 Nov 2026); Danish only (other locales 404).
  "/fotobog": {
    da: "/fotobog",
  },
  "/blog/what-does-a-wedding-photographer-cost": {
    en: "/blog/what-does-a-wedding-photographer-cost",
    da: "/blog/hvad-koster-en-bryllupsfotograf",
    no: "/blog/hva-koster-en-bryllupsfotograf",
    sv: "/blog/vad-kostar-en-brollopsfotograf",
    de: "/blog/was-kostet-ein-hochzeitsfotograf",
  },
  "/blog/wedding-day-schedule": {
    en: "/blog/wedding-day-schedule",
    da: "/blog/bryllupsdags-tidsplan",
    sv: "/blog/brollopsdags-schema",
    no: "/blog/bryllupsdags-tidsplan",
    de: "/blog/hochzeitsplan",
  },
  "/blog/wedding-traditions": {
    en: "/blog/wedding-traditions",
    da: "/blog/bryllupstraditioner",
    no: "/blog/bryllupstradisjoner",
    sv: "/blog/brollopstraditioner",
    de: "/blog/hochzeitstraditionen",
  },
  "/blog/municipality-wedding": {
    en: "/blog/municipality-wedding",
    da: "/blog/bryllup-paa-raadhuset",
    no: "/blog/bryllup-paa-radhuset",
    sv: "/blog/brollop-pa-kommunen",
    de: "/blog/standesamtliche-trauung",
  },
  "/blog/julefrokost-games": {
    en: "/blog/holiday-party-games",
    da: "/blog/julefrokost-lege",
    no: "/blog/julebord-leker",
    sv: "/blog/julbord-lekar",
    de: "/blog/weihnachtsfeier-spiele",
  },
  "/blog/julefrokost-entertainment": {
    en: "/blog/holiday-party-entertainment",
    da: "/blog/julefrokost-underholdning",
    no: "/blog/julebord-underholdning",
    sv: "/blog/julbord-underhallning",
    de: "/blog/weihnachtsfeier-unterhaltung",
  },
  "/blog/julefrokost-ideas": {
    en: "/blog/holiday-party-ideas",
    da: "/blog/julefrokost-ideer",
    no: "/blog/julebord-ideer",
    sv: "/blog/julbords-ideer",
    de: "/blog/weihnachtsfeier-ideen",
  },
  "/julefrokost": {
    en: "/holiday-party",
    da: "/julefrokost",
    no: "/julebord",
    sv: "/julbord",
    de: "/weihnachtsfeier",
  },
  "/konfirmation": {
    en: "/confirmation",
    da: "/konfirmation",
    no: "/konfirmasjon",
    sv: "/konfirmation",
    de: "/konfirmation",
  },
  "/photowall": {
    en: "/photowall",
    da: "/fotovaeg",
    no: "/fotovegg",
    sv: "/fotovegg",
    de: "/fotowand",
  },
  "/blog/konfirmation-ideas": {
    en: "/blog/confirmation-ideas",
    da: "/blog/konfirmation-ideer",
    no: "/blog/konfirmasjon-ideer",
    sv: "/blog/konfirmation-ideer",
    de: "/blog/konfirmations-ideen",
  },
  "/blog/konfirmationsfotografering": {
    en: "/blog/confirmation-photography",
    da: "/blog/konfirmationsfotografering",
    no: "/blog/konfirmasjon-fotografering",
    sv: "/blog/konfirmation-fotografering",
    de: "/blog/konfirmation-fotografie",
  },
  "/blog/konfirmationsunderholdning": {
    en: "/blog/confirmation-entertainment",
    da: "/blog/konfirmationsunderholdning",
    no: "/blog/konfirmasjon-underholdning",
    sv: "/blog/konfirmation-underhallning",
    de: "/blog/konfirmation-unterhaltung",
  },
  "/blog/konfirmationsdekoration-borddaekning": {
    en: "/blog/confirmation-decoration-table-setting",
    da: "/blog/konfirmationsdekoration-borddaekning",
    no: "/blog/konfirmasjon-dekorasjon-borddekking",
    sv: "/blog/konfirmation-dekoration-borddukning",
    de: "/blog/konfirmation-dekoration-tischdekoration",
  },
  "/blog/konfirmationsindbydelser-takkekort": {
    en: "/blog/confirmation-invitations-thank-you-cards",
    da: "/blog/konfirmationsindbydelser-takkekort",
    no: "/blog/konfirmasjon-invitasjoner-takkekort",
    sv: "/blog/konfirmation-inbjudningar-tackkort",
    de: "/blog/konfirmation-einladungen-dankeskarten",
  },
  "/blog/konfirmationsfest-program-taler": {
    en: "/blog/confirmation-party-program-speeches",
    da: "/blog/konfirmationsfest-program-taler",
    no: "/blog/konfirmasjonsfest-program-taler",
    sv: "/blog/konfirmationsfest-program-tal",
    de: "/blog/konfirmationsfeier-programm-reden",
  },
  "/blog/konfirmation-gifts": {
    en: "/blog/confirmation-gifts",
    da: "/blog/konfirmation-gaver",
    no: "/blog/konfirmasjon-gaver",
    sv: "/blog/konfirmation-presenter",
    de: "/blog/konfirmation-geschenke",
  },
  "/blog/konfirmation-food": {
    en: "/blog/confirmation-food",
    da: "/blog/konfirmation-mad",
    no: "/blog/konfirmasjon-mat",
    sv: "/blog/konfirmation-mat",
    de: "/blog/konfirmation-essen",
  },
  "/blog/konfirmation-music": {
    en: "/blog/confirmation-music",
    da: "/blog/konfirmation-musik",
    no: "/blog/konfirmasjon-musikk",
    sv: "/blog/konfirmation-musik",
    de: "/blog/konfirmation-musik",
  },
  "/blog/konfirmation-venue": {
    en: "/blog/confirmation-venue",
    da: "/blog/konfirmation-lokale",
    no: "/blog/konfirmasjon-lokale",
    sv: "/blog/konfirmation-lokal",
    de: "/blog/konfirmation-location",
  },
  "/blog/konfirmation-outfit": {
    en: "/blog/confirmation-outfit",
    da: "/blog/konfirmation-toej",
    no: "/blog/konfirmasjon-antrekk",
    sv: "/blog/konfirmation-klader",
    de: "/blog/konfirmation-outfit",
  },
  "/blog/christening-ideas": {
    en: "/blog/christening-ideas",
    da: "/blog/barnedab-ideer",
    no: "/blog/barnedap-ideer",
    sv: "/blog/dopfest-ideer",
    de: "/blog/taufe-ideen",
  },
  "/blog/christening-food": {
    en: "/blog/christening-food",
    da: "/blog/barnedab-mad",
    no: "/blog/barnedap-mat",
    sv: "/blog/dopfest-mat",
    de: "/blog/taufe-essen",
  },
  "/blog/christening-gifts": {
    en: "/blog/christening-gifts",
    da: "/blog/barnedab-gaver",
    no: "/blog/barnedap-gaver",
    sv: "/blog/dopfest-presenter",
    de: "/blog/taufe-geschenke",
  },
  "/blog/christening-decoration": {
    en: "/blog/christening-decoration",
    da: "/blog/barnedab-dekoration",
    no: "/blog/barnedap-dekorasjon",
    sv: "/blog/dopfest-dekoration",
    de: "/blog/taufe-dekoration",
  },
  "/blog/christening-photos": {
    en: "/blog/christening-photos",
    da: "/blog/barnedab-billeder",
    no: "/blog/barnedap-bilder",
    sv: "/blog/dopfest-bilder",
    de: "/blog/taufe-fotos",
  },
  "/blog/wedding-speech": {
    en: "/blog/wedding-speech",
    da: "/blog/bryllupstale",
    no: "/blog/bryllupstale",
    sv: "/blog/brolloptal",
    de: "/blog/hochzeitsrede",
  },
  "/blog/wedding-food": {
    en: "/blog/wedding-food",
    da: "/blog/bryllupsmad",
    no: "/blog/bryllupsmat",
    sv: "/blog/brollopmat",
    de: "/blog/hochzeitsessen",
  },
  "/blog/wedding-venue": {
    en: "/blog/wedding-venue",
    da: "/blog/bryllupslokale",
    no: "/blog/bryllupslokale",
    sv: "/blog/brollopslokale",
    de: "/blog/hochzeitslocation",
  },
  "/blog/wedding-flowers": {
    en: "/blog/wedding-flowers",
    da: "/blog/bryllupsblomster",
    no: "/blog/bryllupsblomster",
    sv: "/blog/brollopblommor",
    de: "/blog/hochzeitsblumen",
  },
  "/blog/wedding-gifts": {
    en: "/blog/wedding-gifts",
    da: "/blog/bryllupsgaver",
    no: "/blog/bryllupsgaver",
    sv: "/blog/bryllopspresenter",
    de: "/blog/hochzeitsgeschenke",
  },
  "/blog/bar-mitzvah-party": {
    en: "/blog/bar-mitzvah-party",
    da: "/blog/bar-mitsva-fest",
    no: "/blog/bar-mitsva-fest",
    sv: "/blog/bar-mitsva-fest",
    de: "/blog/bar-mizwa-party",
  },
  "/blog/bar-mitzvah-food": {
    en: "/blog/bar-mitzvah-food",
    da: "/blog/bar-mitsva-mad",
    no: "/blog/bar-mitsva-mat",
    sv: "/blog/bar-mitsva-mat",
    de: "/blog/bar-mizwa-essen",
  },
  "/blog/bar-mitzvah-gifts": {
    en: "/blog/bar-mitzvah-gifts",
    da: "/blog/bar-mitsva-gaver",
    no: "/blog/bar-mitsva-gaver",
    sv: "/blog/bar-mitsva-presenter",
    de: "/blog/bar-mizwa-geschenke",
  },
  "/blog/bar-mitzvah-decoration": {
    en: "/blog/bar-mitzvah-decoration",
    da: "/blog/bar-mitsva-dekoration",
    no: "/blog/bar-mitsva-dekorasjon",
    sv: "/blog/bar-mitsva-dekoration",
    de: "/blog/bar-mizwa-dekoration",
  },
  "/blog/bar-mitzvah-photos": {
    en: "/blog/bar-mitzvah-photos",
    da: "/blog/bar-mitsva-billeder",
    no: "/blog/bar-mitsva-bilder",
    sv: "/blog/bar-mitsva-bilder",
    de: "/blog/bar-mizwa-fotos",
  },
  "/blog/birthday-30": {
    en: "/blog/birthday-30",
    da: "/blog/fodselsdag-30",
    no: "/blog/bursdag-30",
    sv: "/blog/fodelsedag-30",
    de: "/blog/geburtstag-30",
  },
  "/blog/birthday-40": {
    en: "/blog/birthday-40",
    da: "/blog/fodselsdag-40",
    no: "/blog/bursdag-40",
    sv: "/blog/fodelsedag-40",
    de: "/blog/geburtstag-40",
  },
  "/blog/birthday-50": {
    en: "/blog/birthday-50",
    da: "/blog/fodselsdag-50",
    no: "/blog/bursdag-50",
    sv: "/blog/fodelsedag-50",
    de: "/blog/geburtstag-50",
  },
  "/blog/birthday-60": {
    en: "/blog/birthday-60",
    da: "/blog/fodselsdag-60",
    no: "/blog/bursdag-60",
    sv: "/blog/fodelsedag-60",
    de: "/blog/geburtstag-60",
  },
  "/blog/birthday-milestone": {
    en: "/blog/birthday-milestone",
    da: "/blog/rund-fodselsdag",
    no: "/blog/rund-bursdag",
    sv: "/blog/rund-fodelsedag",
    de: "/blog/runder-geburtstag",
  },
  "/blog/wedding-photowall": {
    en: "/blog/wedding-photowall",
    da: "/blog/bryllup-fotovaeg",
    no: "/blog/bryllup-fotovegg",
    sv: "/blog/brollop-fotovagg",
    de: "/blog/hochzeit-fotowand",
  },
  "/blog/birthday-photowall": {
    en: "/blog/birthday-photowall",
    da: "/blog/fodselsdag-fotovaeg",
    no: "/blog/bursdag-fotovegg",
    sv: "/blog/fodelsedag-fotovagg",
    de: "/blog/geburtstag-fotowand",
  },
  "/blog/bar-mitzvah-photowall": {
    en: "/blog/bar-mitzvah-photowall",
    da: "/blog/bar-mitsva-fotovaeg",
    no: "/blog/bar-mitsva-fotovegg",
    sv: "/blog/bar-mitsva-fotovagg",
    de: "/blog/bar-mizwa-fotowand",
  },
  "/blog/christening-photowall": {
    en: "/blog/christening-photowall",
    da: "/blog/barnedab-fotovaeg",
    no: "/blog/barnedap-fotovegg",
    sv: "/blog/dopfest-fotovagg",
    de: "/blog/taufe-fotowand",
  },
  "/blog/konfirmation-photowall": {
    en: "/blog/confirmation-photowall",
    da: "/blog/konfirmation-fotovaeg",
    no: "/blog/konfirmasjon-fotovegg",
    sv: "/blog/konfirmation-fotovagg",
    de: "/blog/konfirmation-fotowand",
  },
  "/barnedab": {
    en: "/christening",
    da: "/barnedab",
    no: "/barnedap",
    sv: "/dopfest",
    de: "/taufe",
  },
  "/bar-mitsva": {
    en: "/bar-mitzvah",
    da: "/bar-mitsva",
    no: "/bar-mitsva",
    sv: "/bar-mitsva",
    de: "/bar-mizwa",
  },
  "/fodselsdag": {
    en: "/birthday",
    da: "/fodselsdag",
    no: "/bursdag",
    sv: "/fodelsedag",
    de: "/geburtstag",
  },
  "/blog/wedding-qr-code": {
    en: "/blog/wedding-qr-code",
    da: "/blog/bryllup-qr-kode",
    no: "/blog/bryllup-qr-kode",
    sv: "/blog/brollop-qr-kod",
    de: "/blog/hochzeit-qr-code",
  },
  // Written in en, da, sv, no, de, fr and es only; other locales 404 (src/lib/blogPostLocales.ts).
  "/blog/collect-wedding-photos-from-guests": {
    en: "/blog/collect-wedding-photos-from-guests",
    da: "/blog/samle-billeder-fra-gaester-bryllup",
    no: "/blog/bryllup-samle-bilder-fra-gjester",
    sv: "/blog/brollop-samla-gasternas-bilder",
    de: "/blog/hochzeitsfotos-gaeste-sammeln",
    fr: "/blog/recuperer-photos-invites-mariage",
    es: "/blog/compartir-fotos-boda-invitados",
  },
  // Written in da, sv and no only; other locales 404 (src/lib/blogPostLocales.ts).
  "/blog/wedding-planning-checklist": {
    da: "/blog/bryllup-tjekliste",
    no: "/blog/bryllup-sjekkliste",
    sv: "/blog/brollop-checklista",
  },
  // Written in en and es only; other locales 404 (src/lib/blogPostLocales.ts).
  "/blog/quinceanera-photo-sharing": {
    en: "/blog/quinceanera-photo-sharing",
    es: "/blog/fotos-quinceanera-invitados",
  },
  // Written in en only; other locales 404 (src/lib/blogPostLocales.ts).
  "/blog/graduation-party-photo-sharing": {
    en: "/blog/graduation-party-photo-sharing",
  },
  "/blog/bridal-shower-photo-sharing": {
    en: "/blog/bridal-shower-photo-sharing",
  },
  // Written in en and da only; other locales 404 (src/lib/blogPostLocales.ts).
  "/blog/when-guests-upload-photos": {
    en: "/blog/when-guests-upload-photos",
    da: "/blog/hvornaar-uploader-gaesterne",
  },
  // Written in da and de only; other locales 404 (src/lib/blogPostLocales.ts).
  "/blog/digital-guestbook": {
    da: "/blog/digital-gaestebog",
    de: "/blog/digitales-gaestebuch",
  },
  "/blog/bar-mitzvah-qr-code": {
    en: "/blog/bar-mitzvah-qr-code",
    da: "/blog/bar-mitsva-qr-kode",
    no: "/blog/bar-mitsva-qr-kode",
    sv: "/blog/bar-mitsva-qr-kod",
    de: "/blog/bar-mizwa-qr-code",
  },
  // Written in da, sv and no only; other locales 404 (src/lib/blogPostLocales.ts).
  "/blog/konfirmation-checklist": {
    da: "/blog/konfirmation-tjekliste",
    no: "/blog/konfirmasjon-sjekkliste",
    sv: "/blog/konfirmation-checklista",
  },
  // Written in da and no only; other locales 404 (src/lib/blogPostLocales.ts).
  "/blog/konfirmation-billedshow": {
    da: "/blog/billedshow-konfirmation",
    no: "/blog/bildefremvisning-konfirmasjon",
  },
  // Written in sv only; other locales 404 (src/lib/blogPostLocales.ts).
  "/blog/wedding-slideshow": {
    sv: "/blog/bildspel-brollop",
  },
  // Written in da only; other locales 404 (src/lib/blogPostLocales.ts).
  "/blog/photo-challenges": {
    da: "/blog/fotoopgaver",
  },
  "/blog/konfirmation-qr-code": {
    en: "/blog/confirmation-qr-code",
    da: "/blog/konfirmation-qr-kode",
    no: "/blog/konfirmasjon-qr-kode",
    sv: "/blog/konfirmation-qr-kod",
    de: "/blog/konfirmation-qr-code",
  },
  "/blog/christening-qr-code": {
    en: "/blog/christening-qr-code",
    da: "/blog/barnedab-qr-kode",
    no: "/blog/barnedap-qr-kode",
    sv: "/blog/dopfest-qr-kod",
    de: "/blog/taufe-qr-code",
  },
  "/blog/birthday-qr-code": {
    en: "/blog/birthday-qr-code",
    da: "/blog/fodselsdag-qr-kode",
    no: "/blog/bursdag-qr-kode",
    sv: "/blog/fodelsedag-qr-kod",
    de: "/blog/geburtstag-qr-code",
  },
  "/blog/wedding-full-quality-uploads": {
    en: "/blog/wedding-full-quality-uploads",
    da: "/blog/bryllup-upload-i-fuld-kvalitet",
    no: "/blog/bryllup-opplasting-i-full-kvalitet",
    sv: "/blog/brollop-uppladdning-i-full-kvalitet",
    de: "/blog/hochzeit-uploads-in-voller-qualitaet",
  },
  "/blog/birthday-full-quality-uploads": {
    en: "/blog/birthday-full-quality-uploads",
    da: "/blog/fodselsdag-upload-i-fuld-kvalitet",
    no: "/blog/bursdag-opplasting-i-full-kvalitet",
    sv: "/blog/fodelsedag-uppladdning-i-full-kvalitet",
    de: "/blog/geburtstag-uploads-in-voller-qualitaet",
  },
  "/blog/bar-mitzvah-full-quality-uploads": {
    en: "/blog/bar-mitzvah-full-quality-uploads",
    da: "/blog/bar-mitsva-upload-i-fuld-kvalitet",
    no: "/blog/bar-mitsva-opplasting-i-full-kvalitet",
    sv: "/blog/bar-mitsva-uppladdning-i-full-kvalitet",
    de: "/blog/bar-mizwa-uploads-in-voller-qualitaet",
  },
  "/blog/christening-full-quality-uploads": {
    en: "/blog/christening-full-quality-uploads",
    da: "/blog/barnedab-upload-i-fuld-kvalitet",
    no: "/blog/barnedap-opplasting-i-full-kvalitet",
    sv: "/blog/dopfest-uppladdning-i-full-kvalitet",
    de: "/blog/taufe-uploads-in-voller-qualitaet",
  },
  "/blog/konfirmation-full-quality-uploads": {
    en: "/blog/confirmation-full-quality-uploads",
    da: "/blog/konfirmation-upload-i-fuld-kvalitet",
    no: "/blog/konfirmasjon-opplasting-i-full-kvalitet",
    sv: "/blog/konfirmation-uppladdning-i-full-kvalitet",
    de: "/blog/konfirmation-uploads-in-voller-qualitaet",
  },
  "/blog/wedding-faster-uploads": {
    en: "/blog/wedding-faster-uploads",
    da: "/blog/bryllup-hurtigere-upload",
    no: "/blog/bryllup-raskere-opplasting",
    sv: "/blog/brollop-snabbare-uppladdning",
    de: "/blog/hochzeit-schnellere-uploads",
  },
  "/blog/birthday-faster-uploads": {
    en: "/blog/birthday-faster-uploads",
    da: "/blog/fodselsdag-hurtigere-upload",
    no: "/blog/bursdag-raskere-opplasting",
    sv: "/blog/fodelsedag-snabbare-uppladdning",
    de: "/blog/geburtstag-schnellere-uploads",
  },
  "/blog/bar-mitzvah-faster-uploads": {
    en: "/blog/bar-mitzvah-faster-uploads",
    da: "/blog/bar-mitsva-hurtigere-upload",
    no: "/blog/bar-mitsva-raskere-opplasting",
    sv: "/blog/bar-mitsva-snabbare-uppladdning",
    de: "/blog/bar-mizwa-schnellere-uploads",
  },
  "/blog/christening-faster-uploads": {
    en: "/blog/christening-faster-uploads",
    da: "/blog/barnedab-hurtigere-upload",
    no: "/blog/barnedap-raskere-opplasting",
    sv: "/blog/dopfest-snabbare-uppladdning",
    de: "/blog/taufe-schnellere-uploads",
  },
  "/blog/konfirmation-faster-uploads": {
    en: "/blog/confirmation-faster-uploads",
    da: "/blog/konfirmation-hurtigere-upload",
    no: "/blog/konfirmasjon-raskere-opplasting",
    sv: "/blog/konfirmation-snabbare-uppladdning",
    de: "/blog/konfirmation-schnellere-uploads",
  },
  "/blog/wedding-case-story": {
    en: "/blog/wedding-case-story",
    da: "/blog/bryllup-kundehistorie",
    no: "/blog/bryllup-kundehistorie",
    sv: "/blog/brollop-kundberattelse",
    de: "/blog/hochzeit-kundengeschichte",
  },
  "/blog/birthday-case-story": {
    en: "/blog/birthday-case-story",
    da: "/blog/fodselsdag-kundehistorie",
    no: "/blog/bursdag-kundehistorie",
    sv: "/blog/fodelsedag-kundberattelse",
    de: "/blog/geburtstag-kundengeschichte",
  },
  "/blog/bar-mitzvah-case-story": {
    en: "/blog/bar-mitzvah-case-story",
    da: "/blog/bar-mitsva-kundehistorie",
    no: "/blog/bar-mitsva-kundehistorie",
    sv: "/blog/bar-mitsva-kundberattelse",
    de: "/blog/bar-mizwa-kundengeschichte",
  },
  "/blog/christening-case-story": {
    en: "/blog/christening-case-story",
    da: "/blog/barnedab-kundehistorie",
    no: "/blog/barnedap-kundehistorie",
    sv: "/blog/dopfest-kundberattelse",
    de: "/blog/taufe-kundengeschichte",
  },
  "/blog/konfirmation-case-story": {
    en: "/blog/confirmation-case-story",
    da: "/blog/konfirmation-kundehistorie",
    no: "/blog/konfirmasjon-kundehistorie",
    sv: "/blog/konfirmation-kundberattelse",
    de: "/blog/konfirmation-kundengeschichte",
  },
} as const;

type LocalizedPathnameConfig = Record<string, string>;

function mergeLocalizedPathnames<
  const Base extends Record<string, string | LocalizedPathnameConfig>,
>(base: Base) {
  return Object.fromEntries(
    Object.entries(base).map(([pathname, value]) => {
      const additions = {
        ...additionalLocalizedPathnames[pathname],
        ...additionalNordicPathnames[pathname],
      };
      return [
        pathname,
        typeof value === "string" || Object.keys(additions).length === 0
          ? value
          : { ...value, ...additions },
      ];
    })
  ) as {
    [Key in keyof Base]: Base[Key] extends string
      ? Base[Key]
      : Base[Key] & LocalizedPathnameConfig;
  };
}

export const pathnames = mergeLocalizedPathnames(basePathnames);
